<template>
  <div class="doc-viewer-container" :class="{ 'is-mobile': isMobile }">
    <!-- 加载状态 -->
    <div v-if="loading" class="loading-state">
      <i class="el-icon-loading" style="font-size: 32px; color: #409EFF;"></i>
      <p>加载中...</p>
    </div>
    
    <!-- 空状态 -->
    <div v-else-if="!document.title" class="empty-state">
      <el-empty :description="isEmptyStateDescription">
        <!-- <el-button type="primary" @click="handleCreateDocument" v-if="canEdit">
          <i class="el-icon-plus"></i> 新建文档
        </el-button> -->
      </el-empty>
    </div>
    
    <!-- 文档内容 -->
    <div v-else class="doc-viewer">
      <!-- 工具栏 -->
      <div class="doc-toolbar">
        <div class="toolbar-left">
          <!-- <el-breadcrumb separator="/">
            <el-breadcrumb-item :to="{ path: '/doc' }">文档首页</el-breadcrumb-item>
            <el-breadcrumb-item v-if="document.categoryName">{{ document.categoryName }}</el-breadcrumb-item>
            <el-breadcrumb-item>{{ document.title }}</el-breadcrumb-item>
          </el-breadcrumb> -->
        </div>
        
        <div class="toolbar-right">
          <el-button 
            v-if="isAdminUser" 
            :type="isMobile ? 'text' : 'success'"
            :size="isMobile ? 'mini' : 'medium'" 
            icon="el-icon-share" 
            @click="showShareDialog"
          >
            生成分享链接
          </el-button>
          <el-button 
            v-if="canEdit" 
            :type="isMobile ? 'text' : 'warning'" 
            :size="isMobile ? 'mini' : 'medium'" 
            icon="el-icon-document-copy" 
            @click="handleCopy"
          >
            复制文档
          </el-button>
          <el-button 
            v-if="canEdit" 
            :type="isMobile ? 'text' : 'primary'" 
            :size="isMobile ? 'mini' : 'medium'" 
            icon="el-icon-edit" 
            @click="handleEdit"
          >
            编辑文档
          </el-button>
        </div>
      </div>
      
      <!-- 浮动按钮（仅移动端显示） -->
      <transition name="el-fade-in">
        <div v-if="false" class="floating-actions">
        </div>
      </transition>
      
      <!-- 文档主体 -->
      <div class="doc-main">
        <!-- 文档内容 -->
        <div class="doc-content-wrapper">
          <article class="doc-article">
            <!-- 文档头部 -->
            <header class="doc-header">
              <h1 class="doc-title">{{ document.title }}</h1>
              <div class="doc-meta">
                <span class="meta-item">
                  <el-avatar :size="24" icon="el-icon-user-solid"></el-avatar>
                  <span>{{ document.creatorName || '未知' }}</span>
                </span>
                <span class="meta-item">
                  <i class="el-icon-time"></i>
                  <span>{{ formatDate(document.updateTime) }}</span>
                </span>
                <span class="meta-item" v-if="document.categoryName">
                  <i class="el-icon-folder"></i>
                  <span>{{ document.categoryName }}</span>
                </span>
              </div>
              
              <!-- 标签显示（仅admin和editor可见） -->
              <div v-if="canViewTags && documentTags.length > 0" class="doc-tags">
                <div class="tags-label">
                  <i class="el-icon-price-tag"></i>
                  <span>关键词：</span>
                </div>
                <div class="tags-list">
                  <el-tag
                    v-for="(tag, index) in documentTags"
                    :key="index"
                    size="small"
                    class="tag-item"
                  >
                    {{ tag }}
                  </el-tag>
                </div>
              </div>
            </header>
            
            <!-- 分隔线 -->
            <el-divider></el-divider>
            
            <!-- 文档正文 -->
            <div class="doc-body ql-editor" v-html="document.content" @click="handleContentClick"></div>
            
            <!-- 文档底部 -->
            <footer class="doc-footer">
              <el-divider></el-divider>
              <div class="footer-info">
                <span>创建时间：{{ formatDate(document.createTime) }}</span>
                <span>最后更新：{{ formatDate(document.updateTime) }}</span>
              </div>
            </footer>
          </article>
        </div>
      </div>
    </div>
    
    <!-- 预览组件（仅移动端使用） -->
    <PreviewImageOverlay 
      v-if="previewOverlay.visible && isMobile"
      :src="previewOverlay.src"
      @close="previewOverlay.visible = false"
    />
    
    <!-- 图片查看器（仅电脑端使用） -->
    <ElImageViewer
      v-if="showImageViewer && !isMobile"
      :on-close="closeImageViewer"
      :url-list="previewImages"
      :initial-index="currentImageIndex"
    />
    
    <!-- 生成分享链接对话框 -->
    <el-dialog
      title="生成分享链接"
      :visible.sync="shareDialogVisible"
      :width="isMobile ? '90vw' : '520px'"
      :close-on-click-modal="false"
      custom-class="share-dialog"
    >
      <el-form :model="shareForm" label-width="80px">
        <el-form-item label="到期时间">
          <div style="display:flex; gap:8px; align-items:center; width:100%;">
            <el-date-picker
              v-model="shareForm.expireAt"
              type="datetime"
              placeholder="选择到期时间"
              :disabled="shareForm.neverExpire"
              :picker-options="expirePickerOptions"
              style="flex:1;"
      />
            <el-checkbox v-model="shareForm.neverExpire">永久</el-checkbox>
      </div>
          <div style="margin-top: 6px; color:#909399; font-size:12px;">必须选择大于当前时间；否则无法生成。</div>
        </el-form-item>
        <el-form-item label="分享链接" v-if="shareUrl">
          <el-input 
            v-model="shareUrl" 
            readonly
            style="width: 100%"
          >
            <el-button 
              slot="append" 
              icon="el-icon-document-copy" 
              @click="copyShareUrl"
            >复制</el-button>
          </el-input>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="shareDialogVisible = false">关闭</el-button>
        <el-button type="primary" @click="handleCreateShare" :loading="shareCreating">
          {{ shareUrl ? '重新生成' : '生成链接' }}
        </el-button>
    </div>
    </el-dialog>
  </div>
</template>

<script>
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import { DOCUMENT_PATHS, DOCUMENT_SHARE_PATHS } from '@/api/paths'
import { authUtils } from '@/utils/auth'
import PreviewImageOverlay from '@/components/PreviewImageOverlay.vue'
import ElImageViewer from 'element-ui/packages/image/src/image-viewer'

export default {
  name: 'DocView',
  components: {
    PreviewImageOverlay,
    ElImageViewer
  },
  data() {
    return {
      document: {
        title: '',
        content: '',
        creatorName: '',
        categoryName: '',
        createTime: null,
        updateTime: null,
        keywords: ''
      },
      loading: false,
      showImageViewer: false,
      previewImages: [],
      currentImageIndex: 0,
      // 新的预览组件状态
      previewOverlay: {
        visible: false,
        src: ''
      },
      mobileImagePreview: {
        visible: false,
        url: '',
        scale: 1,
        lastClickTime: 0,
        translateX: 0,
        translateY: 0,
        isDragging: false,
        startX: 0,
        startY: 0,
        lastX: 0,
        lastY: 0,
        hasZoomed: false,
        clickTimer: null,
        fitToHeight: false,
        viewportW: 0,
        viewportH: 0,
        imageAspect: 1,
        pinchStartDist: 0,
        pinchStartScale: 1
      },
      shareDialogVisible: false,
      shareForm: {
        expireAt: null,
        neverExpire: false
      },
      shareUrl: '',
      shareCreating: false,
      currentScrollPosition: 0,
      contentScrollEl: null,
      totalScrollHeight: 0,
      viewportHeight: 0,
      screenWidth: window.innerWidth,
      expirePickerOptions: {
        disabledDate(time) {
          // 禁用今天之前的日期
          const startOfToday = new Date()
          startOfToday.setHours(0,0,0,0)
          return time.getTime() < startOfToday.getTime()
        }
      }
    }
  },
  computed: {
    canEdit() {
      return authUtils.isEditor()
    },
    isAdminUser() {
      return authUtils.isAdmin()
    },
    canViewTags() {
      // 只有admin和editor可以看到标签
      return authUtils.isAdmin() || authUtils.isEditor()
    },
    documentTags() {
      // 将路径格式的keywords转换为标签数组
      if (!this.document.keywords || this.document.keywords.trim() === '') {
        return []
      }
      // 移除开头的斜杠并分割
      return this.document.keywords.replace(/^\//, '').split('/').filter(tag => tag.trim() !== '')
    },
    isInCollection() {
      // DocView 现在专门用于文集内容显示
      return this.$route.path.startsWith('/collections/')
    },
    collectionId() {
      // 文集ID总是从父路由的params.id获取
      return this.$route.params.id
    },
    documentId() {
      // 文档ID从当前路由的params.docId获取
      return this.$route.params.docId
    },
    isEmptyStateDescription() {
      return '请从左侧目录选择文档'
    },
    isMobile() {
      // 优先使用设备检测，避免横屏时被误判为桌面端
      if (typeof navigator !== 'undefined') {
        const ua = navigator.userAgent || navigator.vendor || ''
        
        // 1. 检测明确的移动设备标识
        const mobileRegex = /(android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini)/i
        if (mobileRegex.test(ua)) {
          return true
        }
        
        // 2. 检测移动设备的User Agent特征
        const mobilePlatforms = /Mobile|mobile|Mobi|mobi/i
        if (mobilePlatforms.test(ua)) {
          return true
        }
        
        // 3. 检测触摸设备并结合屏幕尺寸
        if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
          // 使用屏幕的最小尺寸来判断，避免横屏影响
          const screenMinSize = window.screen ? Math.min(window.screen.width, window.screen.height) : 0
          const screenMaxSize = window.screen ? Math.max(window.screen.width, window.screen.height) : 0
          
          // 如果最小尺寸小于等于768，或者最大尺寸小于等于1024，很可能是移动设备
          if (screenMinSize <= 768 || (screenMaxSize <= 1024 && screenMinSize <= 768)) {
            return true
          }
        }
      }
      
      // 4. 兜底：如果以上都不满足，但当前窗口宽度小于等于768，也认为是移动端
      // 这主要是为了处理一些特殊情况和桌面端的小窗口
      return this.screenWidth <= 768
    }
  },
  watch: {
    '$route.params.docId': {
      immediate: true,
      handler(newDocId) {
        // 只有当docId存在且不是'create'时才加载文档
        // 'create'是创建文档的路由，应该由DocEdit组件处理
        if (newDocId && newDocId !== 'create') {
          this.loadDocument()
        }
      }
    },
    screenWidth(newWidth) {
      this.screenWidth = newWidth
    }
  },
  mounted() {
    // 初次挂载时尝试绑定滚动监听
    this.$nextTick(() => {
      this.attachContentScrollListener()
    })
    // 屏幕宽度监听用于区分移动端与桌面端
    this.__onResize = () => { this.screenWidth = window.innerWidth }
    window.addEventListener('resize', this.__onResize)
  },
  beforeDestroy() {
    this.detachContentScrollListener()
    document.removeEventListener('mousemove', this.handleImageMouseMove)
    document.removeEventListener('mouseup', this.handleImageMouseUp)
    if (this.__onResize) {
      window.removeEventListener('resize', this.__onResize)
    }
  },
  methods: {
    async loadDocument() {
      if (!this.documentId) return
      
      this.detachContentScrollListener()
      this.loading = true
      try {
        const response = await this.$get(DOCUMENT_PATHS.GET_DOCUMENT + '?documentId=' + this.documentId)
        if (response.data && response.data.code === 200) {
          const data = response.data.data
          this.document = {
            title: data.document.title,
            content: data.document.content || '<p class="empty-tip">暂无内容</p>',
            creatorName: data.document.creatorName || '',
            categoryName: data.document.categoryName || '',
            createTime: data.document.createTime,
            updateTime: data.document.updateTime,
            keywords: data.document.keywords || ''
          }
          this.$nextTick(() => {
            this.attachContentScrollListener()
            this.updateCurrentScrollPosition()
          })
        }
      } catch (error) {
        this.$message.error('加载文档失败')
        console.error(error)
      } finally {
        this.loading = false
      }
    },
    
    handleEdit() {
      // DocView 现在专门用于文集内容显示
      this.$router.push(`/collections/${this.collectionId}/doc/${this.documentId}/edit`)
    },
    
    async handleCopy() {
      if (!this.documentId) return
      try {
        await this.$confirm(`确定要复制当前文档 "${this.document.title}" 吗？`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'info'
        })
        
        this.loading = true
        const response = await this.$post(DOCUMENT_PATHS.COPY_DOCUMENT, {
          documentId: this.documentId
        })
        
        if (response.data && response.data.code === 200) {
          this.$message.success('复制成功')
          const newDoc = response.data.data
          // 通知父组件（DocumentLayout）刷新目录树
          this.$emit('refresh')
          if (newDoc && newDoc.documentId) {
            const collectionId = this.collectionId || this.$route.params.id
            if (this.$route.path.startsWith('/admin')) {
              this.$router.push(`/admin/collections/${collectionId}/doc/${newDoc.documentId}`)
            } else {
              this.$router.push(`/collections/${collectionId}/doc/${newDoc.documentId}`)
            }
          }
        } else {
          this.$message.error(response.data.msg || '复制失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('复制失败')
          console.error(error)
        }
      } finally {
        this.loading = false
      }
    },
    
    handleCreateDocument() {
      // DocView 现在专门用于文集内容显示
      this.$router.push(`/collections/${this.collectionId}/doc/create`)
    },
    
    formatDate(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    
    // 处理内容点击事件
    handleContentClick(event) {
      if (event.target.tagName === 'IMG') {
        const img = event.target
        if (this.isMobile) {
          // 移动端使用 PreviewImageOverlay
          this.previewOverlay.src = img.src
          this.previewOverlay.visible = true
        } else {
          // 电脑端使用 Element UI 的图片查看器
          // 收集所有图片
          const allImages = this.$el.querySelectorAll('.doc-body img')
          this.previewImages = Array.from(allImages).map(imgEl => imgEl.src)
          this.currentImageIndex = Array.from(allImages).indexOf(img)
          this.showImageViewer = true
        }
      }
    },
    
    // 在指定视口点放大/还原，保持焦点稳定
    zoomAt(viewX, viewY, targetScale) {
      const t = this.previewOverlay
      const vpW = t.viewportW
      const vpH = t.viewportH
      const pX = viewX - vpW / 2
      const pY = viewY - vpH / 2
      const oldScale = t.scale || 1
      const newScale = Math.max(1, Math.min(targetScale, 5))
      // translate' = p*(1 - new/old) + translate*(new/old)
      const ratio = newScale / oldScale
      t.translateX = pX * (1 - ratio) + t.translateX * ratio
      t.translateY = pY * (1 - ratio) + t.translateY * ratio
      t.scale = newScale
      t.hasZoomed = newScale > 1
      t.fitToHeight = newScale > 1
      this.clampPreviewTranslate()
    },
    
    // 预览图片点击与双击（模仿微信：双击在点击点放大/还原）
    handlePreviewImageClick(e) {
      const now = Date.now()
      const t = this.previewOverlay
      const diff = now - (t.lastClickTime || 0)
      t.lastClickTime = now
      if (diff < 300) {
        // 双击切换
        const target = t.hasZoomed ? 1 : 2
        const clientX = e.clientX || (e.touches && e.touches[0]?.clientX) || t.viewportW / 2
        const clientY = e.clientY || (e.touches && e.touches[0]?.clientY) || t.viewportH / 2
        this.zoomAt(clientX, clientY, target)
        if (t.clickTimer) { clearTimeout(t.clickTimer); t.clickTimer = null }
        return
      }
      // 单击关闭（延时以便识别双击）
      if (t.clickTimer) { clearTimeout(t.clickTimer) }
      t.clickTimer = setTimeout(() => {
        this.closePreviewImageOverlay()
        t.clickTimer = null
      }, 320)
    },
    
    // 触摸开始（含捏合、下滑关闭）
    handleImageTouchStart(event) {
      const t = this.previewOverlay
      if (event.touches && event.touches.length === 2) {
        const [a, b] = event.touches
        t.pinchStartDist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)
        t.pinchStartScale = t.scale || 1
        t.hasZoomed = true
        t.fitToHeight = true
        return
      }
      // 记录下滑关闭起点（scale==1时）
      if ((t.scale || 1) === 1) {
        const touch = event.touches[0]
        t.isDragging = true
        t.startY = touch.clientY
        t.startX = touch.clientX
        t.lastY = touch.clientY
        t.lastX = touch.clientX
        return
      }
      // 放大后拖动
      if (t.fitToHeight || t.hasZoomed) {
        t.isDragging = true
        const touch = event.touches[0]
        t.startX = touch.clientX - t.translateX
        t.startY = touch.clientY - t.translateY
        t.lastX = touch.clientX
        t.lastY = touch.clientY
      }
    },
    
    // 触摸移动（捏合缩放/拖动/下滑关闭）
    handleImageTouchMove(event) {
      const t = this.previewOverlay
      if (event.touches && event.touches.length === 2) {
        event.preventDefault()
        const [a, b] = event.touches
        const midX = (a.clientX + b.clientX) / 2
        const midY = (a.clientY + b.clientY) / 2
        const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)
        const ratio = t.pinchStartDist ? (dist / t.pinchStartDist) : 1
        const newScale = Math.max(1, Math.min((t.pinchStartScale || 1) * ratio, 5))
        this.zoomAt(midX, midY, newScale)
        return
      }
      if (t.isDragging) {
        const touch = event.touches[0]
        // 下滑关闭（scale==1 且竖向位移足够且水平位移小）
        if ((t.scale || 1) === 1) {
          const dy = touch.clientY - t.startY
          const dx = touch.clientX - t.startX
          if (Math.abs(dy) > 80 && Math.abs(dx) < 50) {
            this.closePreviewImageOverlay()
            return
          }
          return
        }
        // 放大后的拖动
        event.preventDefault()
        const deltaX = touch.clientX - t.lastX
        const deltaY = touch.clientY - t.lastY
        t.translateX += deltaX
        t.translateY += deltaY
        t.lastX = touch.clientX
        t.lastY = touch.clientY
        this.clampPreviewTranslate()
      }
    },
    handleImageTouchEnd() {
      this.previewOverlay.isDragging = false
    },
    
    // 鼠标按下/移动/抬起（桌面支持与手机一致）
    handleImageMouseDown(event) {
      const t = this.previewOverlay
      if ((t.scale || 1) === 1) {
        // 允许下滑关闭但桌面不常用，忽略
        return
      }
      t.isDragging = true
      t.startX = event.clientX - t.translateX
      t.startY = event.clientY - t.translateY
      t.lastX = event.clientX
      t.lastY = event.clientY
        document.addEventListener('mousemove', this.handleImageMouseMove)
        document.addEventListener('mouseup', this.handleImageMouseUp)
    },
    handleImageMouseMove(event) {
      const t = this.previewOverlay
      if (!t.isDragging) return
      const deltaX = event.clientX - t.lastX
      const deltaY = event.clientY - t.lastY
      t.translateX += deltaX
      t.translateY += deltaY
      t.lastX = event.clientX
      t.lastY = event.clientY
      this.clampPreviewTranslate()
    },
    handleImageMouseUp() {
      this.previewOverlay.isDragging = false
      document.removeEventListener('mousemove', this.handleImageMouseMove)
      document.removeEventListener('mouseup', this.handleImageMouseUp)
    },
    
    // 关闭移动端图片预览
    closePreviewImageOverlay() {
      this.previewOverlay.visible = false
      this.previewOverlay.scale = 1
      this.previewOverlay.translateX = 0
      this.previewOverlay.translateY = 0
      this.previewOverlay.src = ''
      this.previewOverlay.isDragging = false
    },
    
    // 关闭图片预览
    closeImageViewer() {
      this.showImageViewer = false
    },
    
    getScrollContext() {
      // 返回 { type: 'wrapper'|'window', el } 和当前数值
      // 1) 优先内部滚动容器
      let el = this.contentScrollEl || (this.$el && this.$el.querySelector && this.$el.querySelector('.doc-content-wrapper'))
      if (el && el.scrollHeight && el.clientHeight) {
        return { type: 'wrapper', el }
      }
      // 2) 兜底使用窗口滚动
      return { type: 'window', el: null }
    },
    computeScrollMetrics() {
      const ctx = this.getScrollContext()
      if (ctx.type === 'wrapper' && ctx.el) {
        const wrapper = ctx.el
        this.currentScrollPosition = Math.round(wrapper.scrollTop || 0)
        this.totalScrollHeight = Math.max(wrapper.scrollHeight || 0, wrapper.clientHeight || 0, wrapper.offsetHeight || 0)
        this.viewportHeight = wrapper.clientHeight || 0
        // 缓存起来便于下次使用
        this.contentScrollEl = wrapper
      } else {
        // window 模式
        const docEl = document.documentElement
        const body = document.body
        const scrollY = window.scrollY || window.pageYOffset || docEl.scrollTop || body.scrollTop || 0
        const total = Math.max(body.scrollHeight, docEl.scrollHeight)
        const vh = window.innerHeight || docEl.clientHeight || 0
        this.currentScrollPosition = Math.round(scrollY)
        this.totalScrollHeight = total
        this.viewportHeight = vh
      }
    },
    updateCurrentScrollPosition() {
      const retry = (times = 2, delay = 50) => {
        this.$nextTick(() => {
          this.computeScrollMetrics()
          if (times > 0) {
            setTimeout(() => retry(times - 1, delay), delay)
          }
        })
      }
      retry(2, 80)
    },
    // 显示分享对话框
    showShareDialog() {
      this.shareDialogVisible = true
      this.shareUrl = ''
    },
    
    // 创建分享链接
    async handleCreateShare() {
      if (!this.documentId) {
        this.$message.warning('文档ID不存在')
        return
      }
      this.updateCurrentScrollPosition()
      this.shareCreating = true
      try {
        const params = { documentId: this.documentId }
        if (!this.shareForm.neverExpire) {
          if (!this.shareForm.expireAt) {
            this.$message.warning('请选择到期时间')
            this.shareCreating = false
            return
          }
          const ts = new Date(this.shareForm.expireAt).getTime()
          if (!isFinite(ts) || ts <= Date.now()) {
            this.$message.warning('到期时间必须大于当前时间')
            this.shareCreating = false
            return
          }
          params.expireAt = ts
        }
        // 始终从顶部开始
        params.scrollPosition = 0
        const response = await this.$post(DOCUMENT_SHARE_PATHS.CREATE_SHARE, params)
        if (response.data && response.data.code === 200) {
          const payload = response.data.data || {}
          const token = payload.shareToken || ''
          const absoluteUrl = token ? new URL(`/share/${token}`, window.location.origin).toString() : (payload.shareUrl || '')
          this.shareUrl = absoluteUrl
          this.$message.success('分享链接生成成功')
        } else {
          this.$message.error(response.data.msg || '生成分享链接失败')
        }
      } catch (error) {
        console.error('生成分享链接失败:', error)
        this.$message.error('生成分享链接失败')
      } finally {
        this.shareCreating = false
      }
    },
    
    // 复制分享链接
    copyShareUrl() {
      const input = document.createElement('input')
      input.value = this.shareUrl
      document.body.appendChild(input)
      input.select()
      try {
        document.execCommand('copy')
        this.$message.success('链接已复制到剪贴板')
      } catch (err) {
        this.$message.error('复制失败，请手动复制')
      }
      document.body.removeChild(input)
    },
    
    attachContentScrollListener() {
      const wrapper = this.$el ? this.$el.querySelector('.doc-content-wrapper') : null
      if (!wrapper) {
        return
      }
      if (this.contentScrollEl) {
        this.contentScrollEl.removeEventListener('scroll', this.handleContentScroll)
      }
      this.contentScrollEl = wrapper
      this.contentScrollEl.addEventListener('scroll', this.handleContentScroll)
    },
    
    detachContentScrollListener() {
      if (this.contentScrollEl) {
        this.contentScrollEl.removeEventListener('scroll', this.handleContentScroll)
        this.contentScrollEl = null
      }
    },
    
    handleContentScroll(event) {
      this.currentScrollPosition = Math.round(event.target.scrollTop)
    }
  }
}
</script>

<style scoped>
.doc-viewer-container {
  height: 100%;
  background: #f5f7f9;
}

/* ===== 加载和空状态 ===== */
.loading-state,
.empty-state {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
}

/* ===== 工具栏 ===== */
.doc-toolbar {
  height: 50px;
  background: #fff;
  border-bottom: 1px solid #e4e7ed;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
}

.toolbar-left {
  flex: 1;
}

.toolbar-right {
  display: flex;
  gap: 10px;
}

/* ===== 文档主体 ===== */
.doc-main {
  display: flex;
  height: calc(100% - 50px);
  background: #f5f7f9;
}

.doc-content-wrapper {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.doc-main.with-outline .doc-content-wrapper {
  max-width: calc(100% - 280px);
}

/* ===== 文章样式 ===== */
.doc-article {
  /* max-width: 900px; */
  margin: 0 auto;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  padding: 40px 50px;
}

.doc-header {
  margin-bottom: 20px;
}

.doc-title {
  font-size: 32px;
  font-weight: 600;
  color: #303133;
  line-height: 1.4;
  margin: 0 0 20px 0;
  word-break: break-word;
}

.doc-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  color: #909399;
  font-size: 14px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.meta-item i {
  font-size: 16px;
}

/* ===== 标签样式 ===== */
.doc-tags {
  margin-top: 16px;
  display: flex;
}

.tags-label {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #606266;
  font-size: 13px;
  font-weight: 500;
  /* margin-bottom: 8px; */
}

.tags-label i {
  font-size: 14px;
  color: #409EFF;
}

.tags-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.tag-item {
  background: #ecf5ff;
  color: #409EFF;
  border: 1px solid #d9ecff;
  font-size: 12px;
}

/* ===== 文档正文 ===== */
.doc-body {
  font-size: 16px;
  line-height: 1.8;
  color: #303133;
  min-height: 300px;
  padding: 0 !important;
  max-width: 80%;
  /* margin: 0 auto; */
}

.doc-body ::v-deep .empty-tip {
  color: #909399;
  text-align: center;
  padding: 60px 0;
}

.doc-body ::v-deep h1 {
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 16px;
  padding-bottom: 10px;
  border-bottom: 2px solid #e4e7ed;
  color: #303133;
}

.doc-body ::v-deep h2 {
  font-size: 24px;
  font-weight: 600;
  margin: 26px 0 14px;
  padding-left: 12px;
  border-left: 4px solid #409EFF;
  color: #303133;
}

.doc-body ::v-deep h3 {
  font-size: 20px;
  font-weight: 600;
  margin: 22px 0 12px;
  color: #303133;
}

.doc-body ::v-deep h4,
.doc-body ::v-deep h5,
.doc-body ::v-deep h6 {
  font-size: 18px;
  font-weight: 600;
  margin: 18px 0 10px;
  color: #303133;
}

.doc-body ::v-deep p {
  margin: 12px 0;
  line-height: 1.8;
}

.doc-body ::v-deep img {
  max-width: 100%;
  width: auto;
  height: auto;
  max-height: 380px;
  margin: 20px 0;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  cursor: zoom-in;
  display: block;
  object-fit: contain;
}

.doc-body ::v-deep blockquote {
  border-left: 4px solid #409EFF;
  background: #ecf5ff;
  padding: 12px 16px;
  margin: 16px 0;
  color: #606266;
  border-radius: 4px;
}

.doc-body ::v-deep pre {
  background: #282c34;
  color: #abb2bf;
  padding: 16px;
  border-radius: 6px;
  overflow-x: auto;
  margin: 16px 0;
  line-height: 1.6;
  word-wrap: break-word;
  word-break: break-all;
  white-space: pre-wrap;
  max-width: 100%;
}

.doc-body ::v-deep code {
  background: #f5f7f9;
  padding: 3px 8px;
  border-radius: 4px;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 14px;
  color: #e96900;
}

.doc-body ::v-deep pre code {
  background: transparent;
  padding: 0;
  color: inherit;
}

.doc-body ::v-deep ul,
.doc-body ::v-deep ol {
  padding-left: 28px;
  margin: 12px 0;
}

.doc-body ::v-deep li {
  margin: 6px 0;
  line-height: 1.8;
}

.doc-body ::v-deep table {
  width: 100%;
  border-collapse: collapse;
  margin: 16px 0;
  border-radius: 4px;
  overflow: hidden;
}

.doc-body ::v-deep table td,
.doc-body ::v-deep table th {
  border: 1px solid #e4e7ed;
  padding: 12px 16px;
}

.doc-body ::v-deep table th {
  background: #f5f7f9;
  font-weight: 600;
  color: #303133;
}

.doc-body ::v-deep table tr:hover {
  background: #fafafa;
}

.doc-body ::v-deep a {
  color: #409EFF;
  text-decoration: none;
  border-bottom: 1px solid transparent;
  transition: border-color 0.3s;
}

.doc-body ::v-deep a:hover {
  border-bottom-color: #409EFF;
}

/* ===== 文档底部 ===== */
.doc-footer {
  margin-top: 40px;
}

.footer-info {
  display: flex;
  justify-content: space-between;
  color: #909399;
  font-size: 13px;
}

/* ===== 滚动条 ===== */
.doc-content-wrapper::-webkit-scrollbar,
.outline-content::-webkit-scrollbar {
  width: 6px;
}

.doc-content-wrapper::-webkit-scrollbar-thumb,
.outline-content::-webkit-scrollbar-thumb {
  background: #d1dbe5;
  border-radius: 3px;
}

.doc-content-wrapper::-webkit-scrollbar-track,
.outline-content::-webkit-scrollbar-track {
  background: transparent;
}

/* ===== 响应式 ===== */
.doc-viewer-container.is-mobile .doc-toolbar {
  padding: 0 15px;
  height: 50px;
}

.doc-viewer-container.is-mobile .doc-content-wrapper {
  padding: 15px;
}

.doc-viewer-container.is-mobile .doc-article {
  padding: 20px 15px;
}

.doc-viewer-container.is-mobile .doc-title {
  font-size: 22px;
}

.doc-viewer-container.is-mobile .doc-meta {
  flex-direction: column;
  gap: 10px;
  align-items: flex-start;
}

.doc-viewer-container.is-mobile .doc-body {
  font-size: 15px;
  max-width: 100%;
}

.doc-viewer-container.is-mobile .footer-info {
  flex-direction: column;
  gap: 8px;
}

@media (max-width: 480px) {
  .doc-toolbar {
    padding: 0 10px;
    height: 50px;
  }
  
  .doc-content-wrapper {
    padding: 10px;
  }
  
  .doc-article {
    padding: 15px 10px;
  }
  
  .doc-title {
    font-size: 18px;
    margin-bottom: 15px;
  }
  
  .doc-body {
    font-size: 14px;
  }
  
  .doc-body ::v-deep h1 {
    font-size: 22px;
  }
  
  .doc-body ::v-deep h2 {
    font-size: 18px;
  }
  
  .doc-body ::v-deep h3 {
    font-size: 16px;
  }
  
  .doc-body ::v-deep img {
    max-width: 100%;
    height: auto;
    margin: 15px 0;
  }
  
  .doc-body ::v-deep pre {
    padding: 12px;
    font-size: 12px;
    word-wrap: break-word;
    word-break: break-all;
    white-space: pre-wrap;
  }
  
  .doc-body ::v-deep code {
    font-size: 12px;
    word-wrap: break-word;
    word-break: break-all;
  }
}

/* 移动端图片预览样式 */
.mobile-image-preview {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  touch-action: none;
}

.preview-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(4px);
}

.preview-image {
  position: relative;
  z-index: 1;
  max-width: 90vw;
  max-height: 90vh;
  width: auto;
  height: auto;
  object-fit: contain;
  user-select: none;
  -webkit-user-select: none;
  pointer-events: auto;
  transform-origin: center center;
  will-change: transform;
  cursor: grab;
  touch-action: none;
}

.preview-image:active {
  cursor: grabbing;
}

.preview-close {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 2;
  width: 44px;
  height: 44px;
  background: rgba(0, 0, 0, 0.6);
  border: 2px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.preview-close:hover {
  background: rgba(0, 0, 0, 0.8);
  border-color: rgba(255, 255, 255, 1);
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
}

.preview-close:active {
  transform: scale(0.95);
}

.preview-close i {
  font-size: 24px;
  color: #fff;
  font-weight: bold;
}

.doc-viewer-container.is-mobile .preview-image {
  max-width: 95vw;
  max-height: 95vh;
}

.doc-viewer-container.is-mobile .preview-close {
  top: 15px;
  right: 15px;
  width: 40px;
  height: 40px;
  background: rgba(0, 0, 0, 0.7);
  border: 2px solid rgba(255, 255, 255, 0.9);
}

.doc-viewer-container.is-mobile .preview-close i {
  font-size: 20px;
}

.floating-actions {
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 2000;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.floating-share-btn,
.floating-edit-btn {
  box-shadow: 0 6px 18px rgba(0,0,0,0.15);
}

.share-dialog {
  max-width: 520px;
  width: 92vw;
}
.share-dialog .el-dialog__body {
  overflow-x: hidden;
  word-break: break-word;
}
.doc-viewer-container.is-mobile .share-dialog {
  width: 100% !important;
  max-width: 100% !important;
}

.doc-viewer-container.is-mobile .share-dialog .el-dialog__body {
  padding: 16px !important;
}
</style>
